import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import {
  clearCachedGoogleAccessToken,
  getCachedGoogleAccessToken,
  getCachedGoogleEmail,
  setCachedGoogleAccessToken,
  setCachedGoogleEmail,
} from './auth';

export type GoogleDocExportFormat = 'pdf' | 'docx' | 'txt';

export interface GoogleConnection {
  email: string | null;
  connected: boolean;
}

export interface GoogleDocResult {
  documentId: string;
  title: string;
  webViewLink: string;
}

const SCOPES = [
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/drive.file',
];

const EXPORT_MIME_TYPES: Record<GoogleDocExportFormat, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
};

const EXPORT_EXTENSIONS: Record<GoogleDocExportFormat, string> = {
  pdf: 'pdf',
  docx: 'docx',
  txt: 'txt',
};

const makeGoogleProvider = () => {
  const provider = new GoogleAuthProvider();
  SCOPES.forEach((scope) => provider.addScope(scope));
  return provider;
};

const parseGoogleError = async (res: Response, fallback: string) => {
  const err = await res.json().catch(() => null);
  const message = err?.error?.message || fallback;
  const code = err?.error?.code || res.status;
  const error = new Error(message) as Error & { status?: number };
  error.status = code;
  return error;
};

async function getAccessToken(): Promise<string> {
  const cached = getCachedGoogleAccessToken();
  if (cached) return cached;

  const auth = getAuth();
  const provider = makeGoogleProvider();
  const result = await signInWithPopup(auth, provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (!credential?.accessToken) throw new Error('No access token received');
  setCachedGoogleAccessToken(credential.accessToken, 3600000);
  const email = result.user.email || null;
  if (email) setCachedGoogleEmail(email);
  return credential.accessToken;
}

export function getGoogleConnection(): GoogleConnection {
  const email = getCachedGoogleEmail();
  return { email, connected: Boolean(email && getCachedGoogleAccessToken()) };
}

export async function connectGoogleAccount(): Promise<GoogleConnection> {
  await getAccessToken();
  const email = getCachedGoogleEmail();
  if (email) setCachedGoogleEmail(email);
  return { email, connected: Boolean(email) };
}

export function disconnectGoogleAccount(): void {
  clearCachedGoogleAccessToken();
}

export async function createGoogleDocFromHtml(
  title: string,
  htmlContent: string
): Promise<GoogleDocResult> {
  const accessToken = await getAccessToken();

  const boundary = '-------314159265358979323846';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;

  const metadata = {
    name: title,
    mimeType: 'application/vnd.google-apps.document',
  };

  const multipartRequestBody =
    delimiter +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    JSON.stringify(metadata) +
    delimiter +
    'Content-Type: text/html; charset=UTF-8\r\n\r\n' +
    htmlContent +
    closeDelimiter;

  const createRes = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body: multipartRequestBody,
  });

  if (!createRes.ok) {
    throw await parseGoogleError(createRes, 'Failed to create Google Doc from HTML');
  }

  const doc = await createRes.json();
  const documentId: string = doc.id;

  return {
    documentId,
    title,
    webViewLink: `https://docs.google.com/document/d/${documentId}/edit`,
  };
}

export async function exportGoogleDoc(
  documentId: string,
  title: string,
  format: GoogleDocExportFormat
): Promise<void> {
  const accessToken = await getAccessToken();
  const mimeType = EXPORT_MIME_TYPES[format];
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${documentId}/export?mimeType=${encodeURIComponent(mimeType)}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  if (!res.ok) {
    throw await parseGoogleError(res, 'Failed to export Google Doc');
  }

  const blob = await res.blob();
  const safeTitle = title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'google_doc';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeTitle}.${EXPORT_EXTENSIONS[format]}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Permanently deletes a file from Google Drive.
 * Used to remove temporary docs created during PDF export.
 * Failure is non-fatal \u2014 call sites should catch and ignore.
 */
export async function deleteGoogleFile(fileId: string): Promise<void> {
  const accessToken = await getAccessToken();
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  // Drive returns 204 No Content on success
  if (!res.ok && res.status !== 204) {
    console.warn('[GDRIVE] deleteGoogleFile: unexpected status', res.status, 'for file', fileId);
  }
}

export async function shareGoogleDoc(
  documentId: string,
  emailAddress: string,
  role: 'reader' | 'writer' = 'reader'
): Promise<void> {
  const accessToken = await getAccessToken();
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${documentId}/permissions?sendNotificationEmail=true`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'user',
        role,
        emailAddress,
      }),
    }
  );

  if (!res.ok) {
    throw await parseGoogleError(res, 'Failed to share Google Doc');
  }
}
